import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from '../components/ui';
import { api, ApiNetworkError } from '../services/api';
import type {
    CashFlowData,
    DashboardSummary,
    DateFilter,
    DistribuicaoCategoria,
} from '../types';
import { useAuth } from './AuthContext';

const defaultSummary: DashboardSummary = {
    totalIncome: 0,
    totalExpenses: 0,
    resultado: 0,
};

function getDefaultDateFilter(): DateFilter {
    return {
        dataInicio: '',
        dataFim: '',
    };
}

interface FinanceContextType {
    // Data
    transactions: any[];
    cashFlowData: CashFlowData[];
    dashboardSummary: DashboardSummary;
    distribuicaoDespesas: DistribuicaoCategoria[];
    distribuicaoReceitas: DistribuicaoCategoria[];
    distribuicaoSubDespesas: DistribuicaoCategoria[];
    distribuicaoSubReceitas: DistribuicaoCategoria[];
    dateFilter: DateFilter;
    setDateFilter: React.Dispatch<React.SetStateAction<DateFilter>>;
    applyDateFilter: (filter: DateFilter) => void;
    isLoading: boolean;

    // Actions
    addTransaction: (transaction: any) => void;
    refreshData: () => Promise<void>;
}

const FinanceContext = createContext<FinanceContextType | undefined>(undefined);

interface FinanceProviderProps {
    children: ReactNode;
}

export function FinanceProvider({ children }: FinanceProviderProps) {
    const { isAuthenticated, filialAtiva } = useAuth();
    const [transactions, setTransactions] = useState<any[]>([]);
    const [cashFlowData, setCashFlowData] = useState<CashFlowData[]>([]);
    const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary>(defaultSummary);
    const [distribuicaoDespesas, setDistribuicaoDespesas] = useState<DistribuicaoCategoria[]>([]);
    const [distribuicaoReceitas, setDistribuicaoReceitas] = useState<DistribuicaoCategoria[]>([]);
    const [distribuicaoSubDespesas, setDistribuicaoSubDespesas] = useState<DistribuicaoCategoria[]>([]);
    const [distribuicaoSubReceitas, setDistribuicaoSubReceitas] = useState<DistribuicaoCategoria[]>([]);
    const [dateFilter, setDateFilter] = useState<DateFilter>(getDefaultDateFilter);
    const [isLoading, setIsLoading] = useState(true);
    const lastErrorToastAt = useRef(0);

    const refreshData = useCallback(async (filter: DateFilter = dateFilter) => {
    if (!isAuthenticated || !filialAtiva) {
        setIsLoading(false);
        return;
    }

    setIsLoading(true);

    try {
        const [summaryData, transactionsData, cashFlowResult] = await Promise.all([
            api.getDashboardSummary(filter.dataInicio, filter.dataFim),       // ✅
            api.getDashboardRecentTransactions(),
            api.getDashboardCashFlow(filter.dataInicio, filter.dataFim),      // ✅
        ]);

        setDashboardSummary(summaryData);
        setTransactions(transactionsData);
        setCashFlowData(cashFlowResult);

        try {
            const [despesas, receitas, subDespesas, subReceitas] = await Promise.all([
                api.getDistribuicaoCategorias('GASTOS', filter.dataInicio, filter.dataFim),
                api.getDistribuicaoCategorias('RECEITA', filter.dataInicio, filter.dataFim),
                api.getDistribuicaoCategorias('GASTOS', filter.dataInicio, filter.dataFim, 'subcategoria'),
                api.getDistribuicaoCategorias('RECEITA', filter.dataInicio, filter.dataFim, 'subcategoria'),
            ]);
            setDistribuicaoDespesas(despesas);
            setDistribuicaoReceitas(receitas);
            setDistribuicaoSubDespesas(subDespesas);
            setDistribuicaoSubReceitas(subReceitas);
        } catch {
            // distribuição pode não existir ainda
        }
    } catch (error) {
        console.error('Erro ao carregar dados do dashboard:', error);
        // Empresa suspensa (402): a tela de bloqueio ja explica, o toast so
        // apareceria por tras dela.
        if (error instanceof Error && error.message === 'ACCOUNT_SUSPENDED') return;
        // Varias chamadas em paralelo podem falhar juntas durante uma queda;
        // throttle para nao empilhar toasts identicos na tela.
        const now = Date.now();
        if (now - lastErrorToastAt.current > 15000) {
            lastErrorToastAt.current = now;
            const msg = error instanceof ApiNetworkError
                ? error.friendlyMessage
                : 'Não foi possível carregar os dados do painel. Tente novamente em instantes.';
            toast(msg, 'error');
        }
    } finally {
        setIsLoading(false);
    }
}, [isAuthenticated, filialAtiva]);

    const addTransaction = useCallback((transaction: any) => {
        const newTransaction = {
            ...transaction,
            id: Date.now().toString(),
        };
        setTransactions((prev) => [newTransaction, ...prev]);
    }, []);

    const applyDateFilter = useCallback((filter: DateFilter) => {
        setDateFilter(filter);
        refreshData(filter);
    }, [refreshData]);

    useEffect(() => {
        if (!isAuthenticated || !filialAtiva) return;
        const allTimeFilter = getDefaultDateFilter();
        setDateFilter(allTimeFilter);
        refreshData(allTimeFilter);
    }, [isAuthenticated, filialAtiva]);

    return (
        <FinanceContext.Provider
            value={{
                transactions,
                cashFlowData,
                dashboardSummary,
                distribuicaoDespesas,
                distribuicaoReceitas,
                distribuicaoSubDespesas,
                distribuicaoSubReceitas,
                dateFilter,
                setDateFilter,
                isLoading,
                addTransaction,
                refreshData,
                applyDateFilter,
            }}
        >
            {children}
        </FinanceContext.Provider>
    );
}

export function useFinance() {
    const context = useContext(FinanceContext);
    if (context === undefined) {
        throw new Error('useFinance must be used within a FinanceProvider');
    }
    return context;
}